import React, { useState, useEffect, useCallback } from 'react'
import { supabase } from '../../supabase'
import ChantierChat from '../chat/ChantierChat'
import OnboardingTour, { isOnboardingDone, resetOnboarding } from '../onboarding/OnboardingTour'

// ─── NOTE SUPABASE ─────────────────────────────────────────────────────────────
// Table chantiers: ALTER TABLE chantiers ADD COLUMN IF NOT EXISTS code TEXT UNIQUE;
// Table profiles: ALTER TABLE profiles ADD COLUMN IF NOT EXISTS abonnement_actif BOOLEAN DEFAULT false;
//                 ALTER TABLE profiles ADD COLUMN IF NOT EXISTS date_expiration TIMESTAMPTZ;
//                 ALTER TABLE profiles ADD COLUMN IF NOT EXISTS email TEXT;
//
// CREATE TABLE IF NOT EXISTS chantier_membres (
//   id uuid DEFAULT gen_random_uuid() PRIMARY KEY,
//   chantier_id uuid REFERENCES chantiers(id) ON DELETE CASCADE,
//   user_id uuid REFERENCES profiles(id) ON DELETE CASCADE,
//   role text DEFAULT 'entreprise',
//   created_at timestamptz DEFAULT now(),
//   UNIQUE(chantier_id, user_id)
// );
// ALTER TABLE chantier_membres ENABLE ROW LEVEL SECURITY;
// CREATE POLICY "membres_all" ON chantier_membres FOR ALL USING (true) WITH CHECK (true);
//
// CREATE TABLE IF NOT EXISTS notifications (
//   id uuid DEFAULT gen_random_uuid() PRIMARY KEY,
//   user_id uuid REFERENCES profiles(id) ON DELETE CASCADE,
//   titre text,
//   contenu text,
//   lu boolean DEFAULT false,
//   created_at timestamptz DEFAULT now()
// );
// ALTER TABLE notifications ENABLE ROW LEVEL SECURITY;
// CREATE POLICY "notifs_owner" ON notifications FOR ALL USING (auth.uid() = user_id) WITH CHECK (auth.uid() = user_id);
//
// Voir ChantierChat.js pour la table messages_chantier
// ─────────────────────────────────────────────────────────────────────────────

function genererCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
  let code = ''
  for (let i = 0; i < 6; i++) {
    code += chars[Math.floor(Math.random() * chars.length)]
  }
  return code
}

const STATUT_LABELS = { en_cours: 'En cours', planifie: 'Planifie', termine: 'Termine', en_pause: 'En pause' }
const STATUT_BADGE = { en_cours: 'badge-blue', planifie: 'badge-orange', termine: 'badge-green', en_pause: 'badge-gray' }
const TACHE_STATUT_LABELS = { en_attente: 'En attente', en_cours: 'En cours', termine: 'Termine', valide: 'Valide', reserve: 'Reserve' }
const TACHE_STATUT_BADGE = { en_attente: 'badge-gray', en_cours: 'badge-blue', termine: 'badge-orange', valide: 'badge-green', reserve: 'badge-orange' }

// ─── Bloc abonnement expire ───────────────────────────────────────────────────

function AlerteAbonnement({ onRenouveler }) {
  return (
    <div style={{
      background: '#fff5f5', border: '1.5px solid #fed7d7', borderRadius: 10,
      padding: '16px 20px', marginBottom: 20,
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
    }}>
      <div>
        <div style={{ fontWeight: 600, color: '#c53030', fontSize: 14, marginBottom: 3 }}>
          Abonnement expire
        </div>
        <div style={{ fontSize: 13, color: '#e53e3e' }}>
          La creation de nouveaux chantiers est bloquee. Renouvelez votre abonnement pour continuer.
        </div>
      </div>
      <button
        onClick={onRenouveler}
        style={{
          background: '#e53e3e', color: 'white', border: 'none', borderRadius: 8,
          padding: '8px 16px', fontWeight: 600, fontSize: 13, cursor: 'pointer', flexShrink: 0,
        }}
      >
        Renouveler 20€/mois
      </button>
    </div>
  )
}

// ─── Modal Nouveau Chantier ───────────────────────────────────────────────────

function ModalNouveauChantier({ onClose, onCreated, userId, profile }) {
  const [form, setForm] = useState({
    nom: '', adresse: '', description: '',
    date_debut: '', date_fin: '', statut: 'planifie',
  })
  const [emails, setEmails] = useState([{ id: 1, value: '' }])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [etape, setEtape] = useState('form') // 'form' | 'resultat'
  const [resultat, setResultat] = useState(null)

  const ajouterEmail = () => setEmails(prev => [...prev, { id: Date.now(), value: '' }])
  const supprimerEmail = (id) => setEmails(prev => prev.filter(e => e.id !== id))
  const changerEmail = (id, val) => setEmails(prev => prev.map(e => e.id === id ? { ...e, value: val } : e))

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError('')

    // Generer code unique
    let code = genererCode()
    for (let i = 0; i < 5; i++) {
      const { data: ex } = await supabase.from('chantiers').select('id').eq('code', code).single()
      if (!ex) break
      code = genererCode()
    }

    // Creer le chantier
    const { data: nouveauChantier, error: errC } = await supabase
      .from('chantiers')
      .insert([{ ...form, progression: 0, architecte_id: userId, code }])
      .select()
      .single()

    if (errC || !nouveauChantier) {
      setError('Erreur creation : ' + (errC?.message || 'inconnue'))
      setLoading(false)
      return
    }

    const chantierId = nouveauChantier.id
    const nomArchi = profile ? ((profile.prenom || '') + ' ' + (profile.nom || '')).trim() : 'votre architecte'

    // Traiter les emails
    const emailsValides = emails.map(e => e.value.trim().toLowerCase()).filter(Boolean)
    const emailsOk = []
    const emailsInconnus = []

    for (const email of emailsValides) {
      const { data: profil } = await supabase
        .from('profiles')
        .select('id, prenom, nom')
        .eq('email', email)
        .single()

      if (profil) {
        // Ajouter membre
        await supabase.from('chantier_membres').upsert([{
          chantier_id: chantierId,
          user_id: profil.id,
          role: 'entreprise',
        }])
        // Envoyer notification
        await supabase.from('notifications').insert([{
          user_id: profil.id,
          titre: 'Nouveau chantier : ' + form.nom,
          contenu: 'Vous avez ete ajoute au chantier "' + form.nom + '" par ' + nomArchi + '. Votre code acces : ' + code,
        }])
        emailsOk.push(email)
      } else {
        emailsInconnus.push(email)
      }
    }

    setLoading(false)
    onCreated()
    setResultat({ chantierId, code, nom: form.nom, emailsOk, emailsInconnus })
    setEtape('resultat')
  }

  const mailtoLink = (email) => {
    const sujet = encodeURIComponent('Invitation Suiveo BTP - ' + (resultat?.nom || ''))
    const corps = encodeURIComponent(
      'Bonjour,\n\nVous avez ete invite a suivre le chantier "' + (resultat?.nom || '') + '" sur Suiveo BTP.\n\n' +
      'Pour acceder a votre espace client, rendez-vous sur l\'application et entrez le code :\n\n' +
      '   ' + (resultat?.code || '') + '\n\n' +
      'Aucune inscription requise, le code suffit.\n\nCordialement'
    )
    return 'mailto:' + email + '?subject=' + sujet + '&body=' + corps
  }

  // ─── Etape resultat ───────────────────────────────────────────────────────────
  if (etape === 'resultat' && resultat) {
    return (
      <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
        <div className="modal">
          <div className="modal-header">
            <h3 className="modal-title">Chantier cree avec succes</h3>
            <button className="modal-close" onClick={onClose}>x</button>
          </div>
          <div className="modal-body">
            <div style={{ background: '#e8f7f2', borderRadius: 10, padding: '14px 18px', marginBottom: 20, display: 'flex', gap: 12, alignItems: 'center' }}>
              <span style={{ fontSize: 24 }}>{'✅'}</span>
              <div>
                <div style={{ fontWeight: 600, color: '#1D9E75', fontSize: 14 }}>{resultat.nom}</div>
                <div style={{ fontSize: 13, color: '#2d3748', marginTop: 2 }}>Code acces client : <strong style={{ fontFamily: 'monospace', fontSize: 16, letterSpacing: 2 }}>{resultat.code}</strong></div>
              </div>
            </div>

            {resultat.emailsOk.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, color: '#2d3748' }}>{'✅'} Entreprises ajoutees et notifiees :</div>
                {resultat.emailsOk.map(em => (
                  <div key={em} style={{ fontSize: 13, color: '#718096', padding: '4px 0', borderBottom: '1px solid #f5f6fa' }}>{em}</div>
                ))}
              </div>
            )}

            {resultat.emailsInconnus.length > 0 && (
              <div>
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8, color: '#c05621' }}>{'⚠️'} Pas de compte trouve pour ces emails — envoyez le code manuellement :</div>
                {resultat.emailsInconnus.map(em => (
                  <div key={em} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: '#fff8f0', borderRadius: 8, marginBottom: 6, border: '1px solid #fbd38d' }}>
                    <span style={{ fontSize: 13, color: '#744210' }}>{em}</span>
                    <a href={mailtoLink(em)} style={{ fontSize: 12, fontWeight: 600, color: '#1D9E75', textDecoration: 'none', background: '#e8f7f2', padding: '4px 10px', borderRadius: 6 }}>
                      Envoyer par email
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-green" onClick={onClose}>Fermer</button>
          </div>
        </div>
      </div>
    )
  }

  // ─── Etape formulaire ─────────────────────────────────────────────────────────
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h3 className="modal-title">Nouveau chantier</h3>
          <button className="modal-close" onClick={onClose}>x</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="error-msg">{error}</div>}
            <div style={{ background: '#e8f7f2', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#1D9E75', marginBottom: 16 }}>
              Un code acces unique a 6 caracteres sera genere automatiquement.
            </div>
            <div className="form-group">
              <label>Nom du chantier *</label>
              <input className="form-input" required value={form.nom}
                onChange={e => setForm({ ...form, nom: e.target.value })}
                placeholder="Ex: Renovation appartement Paris 11" />
            </div>
            <div className="form-group">
              <label>Adresse</label>
              <input className="form-input" value={form.adresse}
                onChange={e => setForm({ ...form, adresse: e.target.value })}
                placeholder="Ex: 15 rue de la Paix, 75011 Paris" />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea className="form-input" value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                rows={2} style={{ resize: 'vertical' }} placeholder="Description du chantier..." />
            </div>
            <div className="grid-2" style={{ gap: 12 }}>
              <div className="form-group">
                <label>Date de debut</label>
                <input className="form-input" type="date" value={form.date_debut}
                  onChange={e => setForm({ ...form, date_debut: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Date de fin prevue</label>
                <input className="form-input" type="date" value={form.date_fin}
                  onChange={e => setForm({ ...form, date_fin: e.target.value })} />
              </div>
            </div>
            <div className="form-group">
              <label>Statut initial</label>
              <select className="form-input" value={form.statut} onChange={e => setForm({ ...form, statut: e.target.value })}>
                <option value="planifie">Planifie</option>
                <option value="en_cours">En cours</option>
              </select>
            </div>

            {/* Section entreprises */}
            <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #e2e8f0' }}>
              <div style={{ fontWeight: 600, fontSize: 13, color: '#2d3748', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
                {'👥'} Inviter des entreprises (optionnel)
              </div>
              <div style={{ fontSize: 12, color: '#a0aec0', marginBottom: 12 }}>
                Entrez les emails des entreprises. Si elles ont un compte, elles recevront une notification. Sinon, vous pourrez leur envoyer le code par email.
              </div>
              {emails.map((em, i) => (
                <div key={em.id} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <input
                    className="form-input"
                    type="email"
                    value={em.value}
                    onChange={e => changerEmail(em.id, e.target.value)}
                    placeholder={'entreprise@exemple.fr'}
                    style={{ flex: 1 }}
                  />
                  {emails.length > 1 && (
                    <button type="button" onClick={() => supprimerEmail(em.id)} style={{
                      background: '#fff5f5', border: '1px solid #fed7d7', color: '#e53e3e',
                      borderRadius: 8, padding: '0 12px', cursor: 'pointer', fontSize: 16, flexShrink: 0,
                    }}>x</button>
                  )}
                </div>
              ))}
              <button type="button" onClick={ajouterEmail} style={{
                background: 'none', border: '1.5px dashed #cbd5e0', borderRadius: 8,
                padding: '8px 14px', color: '#718096', fontSize: 13, cursor: 'pointer', width: '100%', marginTop: 4,
              }}>
                + Ajouter une entreprise
              </button>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-green" disabled={loading}>
              {loading ? 'Creation...' : 'Creer le chantier'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Composant affichage du code chantier ──────────────────────────────────────

function CodeChantier({ code }) {
  const [copie, setCopie] = useState(false)
  if (!code) return null

  const copier = () => {
    navigator.clipboard.writeText(code).then(() => {
      setCopie(true)
      setTimeout(() => setCopie(false), 2000)
    })
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8, marginTop: 12,
      background: '#f5f6fa', borderRadius: 8, padding: '8px 12px',
      border: '1.5px dashed #cbd5e0',
    }}>
      <span style={{ fontSize: 12, color: '#718096', flexShrink: 0 }}>Code acces :</span>
      <span style={{
        fontFamily: 'monospace', fontWeight: 700, fontSize: 16,
        letterSpacing: 3, color: '#1D9E75', flex: 1,
      }}>{code}</span>
      <button
        onClick={copier}
        style={{
          background: copie ? '#1D9E75' : 'white', color: copie ? 'white' : '#718096',
          border: '1px solid #e2e8f0', borderRadius: 6, padding: '3px 10px',
          fontSize: 11, fontWeight: 500, cursor: 'pointer', transition: 'all 0.15s',
        }}
      >
        {copie ? 'Copie !' : 'Copier'}
      </button>
    </div>
  )
}

// ─── Modal Nouvelle Tache ─────────────────────────────────────────────────────

function ModalNouvelleTache({ onClose, onCreated, chantiers }) {
  const [form, setForm] = useState({
    titre: '', description: '', chantier_id: '',
    date_echeance: '', statut: 'en_attente', priorite: 'normale',
    assigned_to: '', assigned_to_nom: '',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [membres, setMembres] = useState([])

  useEffect(() => {
    if (!form.chantier_id) { setMembres([]); return }
    const loadMembres = async () => {
      const { data: membresData } = await supabase
        .from('chantier_membres')
        .select('user_id')
        .eq('chantier_id', form.chantier_id)
      if (!membresData || membresData.length === 0) { setMembres([]); return }
      const userIds = membresData.map(m => m.user_id)
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, prenom, nom, email')
        .in('id', userIds)
      setMembres((profilesData || []).map(p => ({
        id: p.id,
        nom: ((p.prenom || '') + ' ' + (p.nom || '')).trim() || p.email || p.id,
      })))
    }
    loadMembres()
    setForm(f => ({ ...f, assigned_to: '', assigned_to_nom: '' }))
  }, [form.chantier_id])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    const { data: { user } } = await supabase.auth.getUser()
    const payload = {
      titre: form.titre,
      description: form.description,
      chantier_id: form.chantier_id || null,
      date_echeance: form.date_echeance || null,
      statut: form.statut,
      priorite: form.priorite,
      user_id: user ? user.id : null,
      assigned_to: form.assigned_to || null,
      assigned_to_nom: form.assigned_to_nom || null,
    }
    const { error: err } = await supabase.from('taches').insert([payload])
    if (err) { setError('Erreur : ' + err.message); setLoading(false) }
    else { onCreated(); onClose() }
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h3 className="modal-title">Nouvelle tache</h3>
          <button className="modal-close" onClick={onClose}>x</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="error-msg">{error}</div>}
            <div className="form-group">
              <label>Titre *</label>
              <input className="form-input" required value={form.titre}
                onChange={e => setForm({ ...form, titre: e.target.value })}
                placeholder="Ex: Pose du carrelage salle de bain" />
            </div>
            <div className="form-group">
              <label>Chantier</label>
              <select className="form-input" value={form.chantier_id}
                onChange={e => setForm({ ...form, chantier_id: e.target.value })}>
                <option value="">Selectionner un chantier</option>
                {chantiers.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
              </select>
            </div>
            {form.chantier_id && (
              <div className="form-group">
                <label>Assigner a</label>
                <select className="form-input" value={form.assigned_to}
                  onChange={e => {
                    const membre = membres.find(m => m.id === e.target.value)
                    setForm({ ...form, assigned_to: e.target.value, assigned_to_nom: membre ? membre.nom : '' })
                  }}>
                  <option value="">— Non assigne (visible par tous) —</option>
                  {membres.map(m => <option key={m.id} value={m.id}>{m.nom}</option>)}
                </select>
                {membres.length === 0 && (
                  <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 4 }}>
                    Aucune entreprise n'a encore rejoint ce chantier.
                  </div>
                )}
              </div>
            )}
            <div className="form-group">
              <label>Description</label>
              <textarea className="form-input" value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
                rows={3} style={{ resize: 'vertical' }} />
            </div>
            <div className="grid-2" style={{ gap: 12 }}>
              <div className="form-group">
                <label>Priorite</label>
                <select className="form-input" value={form.priorite}
                  onChange={e => setForm({ ...form, priorite: e.target.value })}>
                  <option value="basse">Basse</option>
                  <option value="normale">Normale</option>
                  <option value="haute">Haute</option>
                  <option value="urgente">Urgente</option>
                </select>
              </div>
              <div className="form-group">
                <label>Echeance</label>
                <input className="form-input" type="date" value={form.date_echeance}
                  onChange={e => setForm({ ...form, date_echeance: e.target.value })} />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Annuler</button>
            <button type="submit" className="btn btn-green" disabled={loading}>
              {loading ? 'Creation...' : 'Creer la tache'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Vue Chantiers ────────────────────────────────────────────────────────────

// ─── Modal Editer Chantier ────────────────────────────────────────────────────

function ModalEditerChantier({ chantier, onClose, onSaved }) {
  const [form, setForm] = useState({
    nom: chantier.nom || '',
    adresse: chantier.adresse || '',
    description: chantier.description || '',
    date_debut: chantier.date_debut ? chantier.date_debut.slice(0, 10) : '',
    date_fin: chantier.date_fin ? chantier.date_fin.slice(0, 10) : '',
    statut: chantier.statut || 'planifie',
    progression: chantier.progression || 0,
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    const { error: err } = await supabase.from('chantiers').update({
      nom: form.nom,
      adresse: form.adresse,
      description: form.description,
      date_debut: form.date_debut || null,
      date_fin: form.date_fin || null,
      statut: form.statut,
      progression: Number(form.progression),
    }).eq('id', chantier.id)
    if (err) { setError('Erreur : ' + err.message); setLoading(false) }
    else { onSaved(); onClose() }
  }

  const handleDelete = async () => {
    if (!window.confirm('Supprimer definitivement ce chantier ? Cette action est irreversible.')) return
    await supabase.from('chantiers').delete().eq('id', chantier.id)
    onSaved(); onClose()
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h3 className="modal-title">Modifier le chantier</h3>
          <button className="modal-close" onClick={onClose}>x</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="error-msg">{error}</div>}
            <div className="form-group">
              <label>Nom *</label>
              <input className="form-input" required value={form.nom} onChange={e => setForm({ ...form, nom: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Adresse</label>
              <input className="form-input" value={form.adresse} onChange={e => setForm({ ...form, adresse: e.target.value })} />
            </div>
            <div className="grid-2" style={{ gap: 12 }}>
              <div className="form-group">
                <label>Statut</label>
                <select className="form-input" value={form.statut} onChange={e => setForm({ ...form, statut: e.target.value })}>
                  <option value="planifie">Planifie</option>
                  <option value="en_cours">En cours</option>
                  <option value="termine">Termine</option>
                  <option value="en_pause">En pause</option>
                </select>
              </div>
              <div className="form-group">
                <label>Avancement : {form.progression}%</label>
                <input type="range" min="0" max="100" step="5" value={form.progression}
                  onChange={e => setForm({ ...form, progression: e.target.value })}
                  style={{ width: '100%', accentColor: '#1D9E75' }} />
              </div>
            </div>
            <div className="grid-2" style={{ gap: 12 }}>
              <div className="form-group">
                <label>Date debut</label>
                <input className="form-input" type="date" value={form.date_debut} onChange={e => setForm({ ...form, date_debut: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Date fin prevue</label>
                <input className="form-input" type="date" value={form.date_fin} onChange={e => setForm({ ...form, date_fin: e.target.value })} />
              </div>
            </div>
          </div>
          <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
            <button type="button" onClick={handleDelete} style={{ background: '#fff5f5', color: '#e53e3e', border: '1.5px solid #fed7d7', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
              🗑 Supprimer
            </button>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="btn btn-outline" onClick={onClose}>Annuler</button>
              <button type="submit" className="btn btn-green" disabled={loading}>{loading ? 'Sauvegarde...' : 'Sauvegarder'}</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Modal Editer Tache ───────────────────────────────────────────────────────

function ModalEditerTache({ tache, chantiers, onClose, onSaved }) {
  const [form, setForm] = useState({
    titre: tache.titre || '',
    description: tache.description || '',
    chantier_id: tache.chantier_id || '',
    date_echeance: tache.date_echeance ? tache.date_echeance.slice(0, 10) : '',
    statut: tache.statut || 'en_attente',
    priorite: tache.priorite || 'normale',
  })
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    const { error: err } = await supabase.from('taches').update({
      titre: form.titre,
      description: form.description,
      chantier_id: form.chantier_id || null,
      date_echeance: form.date_echeance || null,
      statut: form.statut,
      priorite: form.priorite,
    }).eq('id', tache.id)
    if (err) { setError('Erreur : ' + err.message); setLoading(false) }
    else { onSaved(); onClose() }
  }

  const handleDelete = async () => {
    if (!window.confirm('Supprimer cette tache ?')) return
    await supabase.from('taches').delete().eq('id', tache.id)
    onSaved(); onClose()
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h3 className="modal-title">Modifier la tache</h3>
          <button className="modal-close" onClick={onClose}>x</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && <div className="error-msg">{error}</div>}
            <div className="form-group">
              <label>Titre *</label>
              <input className="form-input" required value={form.titre} onChange={e => setForm({ ...form, titre: e.target.value })} />
            </div>
            <div className="form-group">
              <label>Chantier</label>
              <select className="form-input" value={form.chantier_id} onChange={e => setForm({ ...form, chantier_id: e.target.value })}>
                <option value="">Aucun</option>
                {chantiers.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea className="form-input" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} style={{ resize: 'vertical' }} />
            </div>
            <div className="grid-2" style={{ gap: 12 }}>
              <div className="form-group">
                <label>Statut</label>
                <select className="form-input" value={form.statut} onChange={e => setForm({ ...form, statut: e.target.value })}>
                  <option value="en_attente">En attente</option>
                  <option value="en_cours">En cours</option>
                  <option value="termine">Termine</option>
                  <option value="valide">Valide</option>
                  <option value="reserve">Reserve</option>
                </select>
              </div>
              <div className="form-group">
                <label>Priorite</label>
                <select className="form-input" value={form.priorite} onChange={e => setForm({ ...form, priorite: e.target.value })}>
                  <option value="basse">Basse</option>
                  <option value="normale">Normale</option>
                  <option value="haute">Haute</option>
                  <option value="urgente">Urgente</option>
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Echeance</label>
              <input className="form-input" type="date" value={form.date_echeance} onChange={e => setForm({ ...form, date_echeance: e.target.value })} />
            </div>
          </div>
          <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
            <button type="button" onClick={handleDelete} style={{ background: '#fff5f5', color: '#e53e3e', border: '1.5px solid #fed7d7', borderRadius: 8, padding: '8px 16px', cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>
              🗑 Supprimer
            </button>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="btn btn-outline" onClick={onClose}>Annuler</button>
              <button type="submit" className="btn btn-green" disabled={loading}>{loading ? 'Sauvegarde...' : 'Sauvegarder'}</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Modal Compte Rendu de Visite ─────────────────────────────────────────────

function ModalCompteRendu({ chantier, taches, membres, onClose }) {
  const today = new Date().toISOString().slice(0, 10)
  const [dateVisite, setDateVisite] = React.useState(today)
  const [participantsCoches, setParticipantsCoches] = React.useState({})
  const [observations, setObservations] = React.useState('')
  const [prochaineVisite, setProchaineVisite] = React.useState('')
  const [obsParTache, setObsParTache] = React.useState({})
  const [generating, setGenerating] = React.useState(false)

  const tachesChantier = taches.filter(t => t.chantier_id === chantier.id)

  const toggleParticipant = (id) => setParticipantsCoches(p => ({ ...p, [id]: !p[id] }))

  const genererPDF = () => {
    setGenerating(true)
    const participantsListe = membres.filter(m => participantsCoches[m.id])
    const dateVF = new Date(dateVisite).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
    const dateGenF = new Date().toLocaleDateString('fr-FR')
    const statutLabel = { en_attente: 'En attente', en_cours: 'En cours', termine: 'Termine', valide: 'Valide', reserve: '⚠️ Reserve' }

    const htmlContent = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8"/>
  <title>Compte Rendu de Visite - ${chantier.nom}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 13px; color: #1a202c; padding: 0; }
    .header { background: #1D9E75; color: white; padding: 28px 40px; display: flex; justify-content: space-between; align-items: flex-start; }
    .header-left h1 { font-size: 22px; font-weight: 800; margin-bottom: 4px; }
    .header-left p { font-size: 13px; opacity: 0.85; }
    .header-right { text-align: right; font-size: 12px; opacity: 0.9; }
    .logo-text { font-size: 16px; font-weight: 800; letter-spacing: -0.3px; }
    .content { padding: 32px 40px; }
    .section { margin-bottom: 28px; }
    .section-title { font-size: 14px; font-weight: 700; color: #1D9E75; text-transform: uppercase; letter-spacing: 0.5px; border-bottom: 2px solid #e8f7f2; padding-bottom: 6px; margin-bottom: 14px; }
    .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
    .info-item { background: #f8fafc; border-radius: 6px; padding: 10px 14px; }
    .info-item label { font-size: 10px; color: #718096; text-transform: uppercase; letter-spacing: 0.5px; display: block; margin-bottom: 3px; }
    .info-item span { font-size: 14px; font-weight: 600; color: #0f172a; }
    table { width: 100%; border-collapse: collapse; margin-top: 8px; }
    th { background: #f0fdf8; color: #1D9E75; font-size: 11px; text-transform: uppercase; letter-spacing: 0.4px; padding: 9px 12px; text-align: left; border: 1px solid #e2e8f0; }
    td { padding: 9px 12px; border: 1px solid #e2e8f0; font-size: 12px; vertical-align: top; }
    tr:nth-child(even) td { background: #fafafa; }
    .badge { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 10px; font-weight: 700; }
    .badge-green { background: #e8f7f2; color: #1D9E75; }
    .badge-blue { background: #eff6ff; color: #3182ce; }
    .badge-orange { background: #fffbeb; color: #92400e; }
    .badge-gray { background: #f5f6fa; color: #718096; }
    .obs-box { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 16px; min-height: 80px; font-size: 14px; line-height: 1.6; white-space: pre-wrap; }
    .footer { margin-top: 40px; padding: 20px 40px; border-top: 2px solid #e2e8f0; display: flex; justify-content: space-between; align-items: flex-end; }
    .signature-box { width: 200px; border-top: 1px solid #718096; padding-top: 8px; text-align: center; font-size: 11px; color: #718096; margin-top: 40px; }
    .print-note { font-size: 10px; color: #a0aec0; text-align: center; margin-top: 20px; }
    @media print {
      body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
      .no-print { display: none !important; }
      .header { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    }
  </style>
</head>
<body>
  <div class="header">
    <div class="header-left">
      <div class="logo-text">Suiveo BTP</div>
      <h1>Compte Rendu de Visite de Chantier</h1>
      <p>${chantier.nom}${chantier.adresse ? ' — ' + chantier.adresse : ''}</p>
    </div>
    <div class="header-right">
      <div>Date de visite</div>
      <div style="font-size:16px;font-weight:800;margin-top:4px;">${dateVF}</div>
      <div style="margin-top:8px;opacity:0.7">Genere le ${dateGenF}</div>
    </div>
  </div>

  <div class="content">
    <div class="section">
      <div class="section-title">Informations generales</div>
      <div class="info-grid">
        <div class="info-item"><label>Chantier</label><span>${chantier.nom}</span></div>
        <div class="info-item"><label>Adresse</label><span>${chantier.adresse || 'Non renseignee'}</span></div>
        <div class="info-item"><label>Avancement</label><span>${chantier.progression || 0}%</span></div>
        <div class="info-item"><label>Code acces client</label><span>${chantier.code}</span></div>
      </div>
    </div>

    <div class="section">
      <div class="section-title">Participants presents (${participantsListe.length})</div>
      ${participantsListe.length === 0
        ? '<p style="color:#718096;font-style:italic">Aucun participant selectionne</p>'
        : `<table>
            <thead><tr><th>Nom</th><th>Email</th><th>Role</th></tr></thead>
            <tbody>
              ${participantsListe.map(m => `
                <tr>
                  <td>${m.prenom || ''} ${m.nom || ''}</td>
                  <td>${m.email || ''}</td>
                  <td>${m.role === 'architecte' ? 'Architecte / MOE' : m.role === 'entreprise' ? 'Entreprise' : m.role}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>`
      }
    </div>

    <div class="section">
      <div class="section-title">Observations generales</div>
      <div class="obs-box">${observations || 'Aucune observation renseignee.'}</div>
    </div>

    <div class="section">
      <div class="section-title">Etat des taches (${tachesChantier.length})</div>
      ${tachesChantier.length === 0
        ? '<p style="color:#718096;font-style:italic">Aucune tache sur ce chantier.</p>'
        : `<table>
            <thead><tr><th style="width:35%">Tache</th><th style="width:15%">Statut</th><th style="width:15%">Priorite</th><th>Observations</th></tr></thead>
            <tbody>
              ${tachesChantier.map(t => {
                const badgeClass = { en_attente: 'badge-gray', en_cours: 'badge-blue', termine: 'badge-orange', valide: 'badge-green', reserve: 'badge-orange' }[t.statut] || 'badge-gray'
                return `
                  <tr>
                    <td style="font-weight:500">${t.titre}</td>
                    <td><span class="badge ${badgeClass}">${statutLabel[t.statut] || t.statut}</span></td>
                    <td>${t.priorite || 'normale'}</td>
                    <td style="color:#4a5568">${obsParTache[t.id] || (t.statut === 'reserve' ? t.description || 'Reserves a lever' : '')}</td>
                  </tr>
                `}).join('')}
            </tbody>
          </table>`
      }
    </div>

    ${prochaineVisite ? `
    <div class="section">
      <div class="section-title">Prochaine visite prevue</div>
      <div class="info-item" style="background:#e8f7f2;border-radius:8px;padding:14px 18px;display:inline-block">
        <label>Date</label>
        <span>${new Date(prochaineVisite).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</span>
      </div>
    </div>` : ''}

    <div class="footer">
      <div>
        <div class="signature-box">Signature Architecte / MOE</div>
      </div>
      <div>
        <div class="signature-box">Signature Entreprise representante</div>
      </div>
      <div>
        <div class="signature-box">Signature Client (si present)</div>
      </div>
    </div>

    <div class="print-note">Document genere par Suiveo BTP — suiveo-btp.fr</div>
  </div>
</body>
</html>`

    const win = window.open('', '_blank')
    win.document.write(htmlContent)
    win.document.close()
    setTimeout(() => {
      win.print()
      setGenerating(false)
    }, 500)
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 640, width: '100%' }}>
        <div className="modal-header">
          <h3 className="modal-title">📋 Compte rendu de visite</h3>
          <button className="modal-close" onClick={onClose}>x</button>
        </div>
        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <div style={{ background: '#e8f7f2', borderRadius: 8, padding: '10px 14px', marginBottom: 20, fontSize: 13, color: '#1D9E75' }}>
            Remplissez le formulaire puis cliquez sur "Generer le PDF". Le document s ouvrira dans un nouvel onglet pour impression.
          </div>

          <div className="form-group">
            <label>Date de visite *</label>
            <input className="form-input" type="date" value={dateVisite} onChange={e => setDateVisite(e.target.value)} />
          </div>

          <div className="form-group">
            <label>Participants presents</label>
            <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: 12 }}>
              {membres.length === 0 ? (
                <div style={{ color: '#a0aec0', fontSize: 13 }}>Aucun membre enregistre sur ce chantier.</div>
              ) : membres.map(m => (
                <label key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!participantsCoches[m.id]}
                    onChange={() => toggleParticipant(m.id)}
                    style={{ width: 16, height: 16, accentColor: '#1D9E75' }}
                  />
                  <span style={{ fontSize: 14 }}>{m.prenom} {m.nom}</span>
                  <span style={{ fontSize: 12, color: '#718096' }}>— {m.email}</span>
                  <span style={{ fontSize: 11, color: '#a0aec0' }}>({m.role})</span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>Observations generales</label>
            <textarea className="form-input" rows={4} style={{ resize: 'vertical' }}
              placeholder="Etat general du chantier, points importants abordes, decisions prises..."
              value={observations} onChange={e => setObservations(e.target.value)} />
          </div>

          {tachesChantier.length > 0 && (
            <div className="form-group">
              <label>Observations par tache</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {tachesChantier.map(t => {
                  const badgeColor = { en_attente: '#718096', en_cours: '#3182ce', termine: '#c05621', valide: '#1D9E75', reserve: '#c05621' }[t.statut] || '#718096'
                  return (
                    <div key={t.id} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '10px 14px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                        <span style={{ fontWeight: 600, fontSize: 13 }}>{t.titre}</span>
                        <span style={{ fontSize: 11, color: badgeColor, fontWeight: 700, background: '#f5f6fa', padding: '1px 8px', borderRadius: 10 }}>
                          {t.statut === 'reserve' ? '⚠️ Reserve' : t.statut}
                        </span>
                      </div>
                      <input className="form-input" style={{ fontSize: 13 }}
                        placeholder="Observation specifique (facultatif)..."
                        value={obsParTache[t.id] || ''}
                        onChange={e => setObsParTache(o => ({ ...o, [t.id]: e.target.value }))} />
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          <div className="form-group">
            <label>Prochaine visite prevue (facultatif)</label>
            <input className="form-input" type="date" value={prochaineVisite} onChange={e => setProchaineVisite(e.target.value)} />
          </div>
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-outline" onClick={onClose}>Annuler</button>
          <button type="button" className="btn btn-green" onClick={genererPDF} disabled={generating || !dateVisite}>
            {generating ? 'Generation...' : '📄 Generer le PDF'}
          </button>
        </div>
      </div>
    </div>
  )
}

function VueChantiers({ chantiers, taches, loading, onNew, onRefresh, abonnementExpire, onDemandeRenouvellement }) {
  const [compteRenduChantier, setCompteRenduChantier] = React.useState(null)
  const [membresChantier, setMembresChantier] = React.useState([])
  const [editerChantier, setEditerChantier] = React.useState(null)

  const ouvrirCompteRendu = async (chantier) => {
    // Charger les membres du chantier
    const { data: membresData } = await supabase
      .from('chantier_membres')
      .select('*, profiles(*)')
      .eq('chantier_id', chantier.id)
    const membres = (membresData || []).map(m => m.profiles).filter(Boolean)
    setMembresChantier(membres)
    setCompteRenduChantier(chantier)
  }

  if (loading) return <div className="loading-state">Chargement des chantiers...</div>

  return (
    <div>
      <div className="section-header">
        <h2 className="section-title">Tous les chantiers ({chantiers.length})</h2>
        <button
          id="step-nouveau-chantier"
          className="btn btn-green"
          onClick={abonnementExpire ? onDemandeRenouvellement : onNew}
          style={abonnementExpire ? { background: '#a0aec0', cursor: 'not-allowed' } : {}}
          title={abonnementExpire ? 'Abonnement expire' : ''}
        >
          {abonnementExpire ? 'Abonnement expire' : '+ Nouveau chantier'}
        </button>
      </div>
      {abonnementExpire && <AlerteAbonnement onRenouveler={onDemandeRenouvellement} />}
      {chantiers.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🏗️</div>
          <div className="empty-state-text">Aucun chantier. Creez votre premier chantier !</div>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {chantiers.map(c => (
            <div key={c.id} className="chantier-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                <div className="chantier-card-title">{c.nom}</div>
                <span className={'badge ' + (STATUT_BADGE[c.statut] || 'badge-gray')}>
                  {STATUT_LABELS[c.statut] || c.statut}
                </span>
              </div>
              {c.adresse && <div className="chantier-card-meta"><span>📍 {c.adresse}</span></div>}
              {c.date_debut && (
                <div className="chantier-card-meta">
                  <span>📅 {new Date(c.date_debut).toLocaleDateString('fr-FR')}</span>
                  {c.date_fin && <span>→ {new Date(c.date_fin).toLocaleDateString('fr-FR')}</span>}
                </div>
              )}
              <div style={{ marginTop: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--gray-500)', marginBottom: 6 }}>
                  <span>Avancement</span>
                  <span style={{ fontWeight: 600, color: 'var(--primary)' }}>{c.progression || 0}%</span>
                </div>
                <div className="progress-bar-wrap">
                  <div className="progress-bar-fill" style={{ width: (c.progression || 0) + '%' }} />
                </div>
              </div>
              <CodeChantier code={c.code} />
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #f0f0f0', display: 'flex', gap: 8 }}>
                <button
                  onClick={() => ouvrirCompteRendu(c)}
                  style={{ flex: 1, padding: '7px 12px', background: '#f0fdf8', border: '1px solid #c6f6d5', borderRadius: 7, fontSize: 13, fontWeight: 600, color: '#1D9E75', cursor: 'pointer' }}
                >
                  📋 CR Visite
                </button>
                <button
                  onClick={() => setEditerChantier(c)}
                  style={{ padding: '7px 14px', background: '#f5f6fa', border: '1px solid #e2e8f0', borderRadius: 7, fontSize: 13, fontWeight: 500, color: '#4a5568', cursor: 'pointer' }}
                >
                  ✏️
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      {compteRenduChantier && (
        <ModalCompteRendu
          chantier={compteRenduChantier}
          taches={taches || []}
          membres={membresChantier}
          onClose={() => setCompteRenduChantier(null)}
        />
      )}
      {editerChantier && (
        <ModalEditerChantier
          chantier={editerChantier}
          onClose={() => setEditerChantier(null)}
          onSaved={() => { onRefresh(); setEditerChantier(null) }}
        />
      )}
    </div>
  )
}

// ─── Vue Taches ───────────────────────────────────────────────────────────────

function VueTaches({ taches, chantiers, loading, onNew, onRefresh }) {
  const [filter, setFilter] = useState('tous')
  const filtered = filter === 'tous' ? taches : taches.filter(t => t.statut === filter)
  const chantiersMap = Object.fromEntries(chantiers.map(c => [c.id, c]))
  const [editerTache, setEditerTache] = useState(null)

  const validerTache = async (id) => {
    await supabase.from('taches').update({ statut: 'valide' }).eq('id', id)
    onRefresh()
  }

  const marquerReserve = async (id) => {
    const note = window.prompt('Decrivez les reserves a lever (obligatoire) :')
    if (!note) return
    await supabase.from('taches').update({ statut: 'reserve', description: note }).eq('id', id)
    onRefresh()
  }

  if (loading) return <div className="loading-state">Chargement des taches...</div>

  return (
    <div>
      <div className="section-header">
        <h2 className="section-title">Taches ({filtered.length})</h2>
        <button className="btn btn-green" onClick={onNew}>+ Nouvelle tache</button>
      </div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { key: 'tous', label: 'Toutes' },
          { key: 'en_attente', label: 'En attente' },
          { key: 'en_cours', label: 'En cours' },
          { key: 'termine', label: 'Terminees' },
          { key: 'valide', label: 'Validees' },
          { key: 'reserve', label: '⚠️ Reserves' },
        ].map(f => (
          <button key={f.key} onClick={() => setFilter(f.key)}
            className="btn btn-outline btn-sm"
            style={filter === f.key ? { background: 'var(--primary)', color: 'white', borderColor: 'var(--primary)' } : {}}>
            {f.label}
          </button>
        ))}
      </div>
      {filtered.length === 0 ? (
        <div className="empty-state"><div className="empty-state-icon">✅</div>
          <div className="empty-state-text">Aucune tache dans cette categorie.</div></div>
      ) : (
        <div className="card">
          <table className="data-table">
            <thead><tr>
              <th>Tache</th><th>Chantier</th><th>Assigne a</th><th>Priorite</th><th>Statut</th><th>Echeance</th><th>Action</th>
            </tr></thead>
            <tbody>
              {filtered.map(t => (
                <tr key={t.id}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{t.titre}</div>
                    {t.description && <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 2 }}>{t.description.slice(0, 60)}</div>}
                  </td>
                  <td style={{ fontSize: 13, color: 'var(--gray-500)' }}>{chantiersMap[t.chantier_id] ? chantiersMap[t.chantier_id].nom : '—'}</td>
                  <td style={{ fontSize: 13 }}>
                    {t.assigned_to_nom
                      ? <span style={{ background: '#e8f7f2', color: '#1D9E75', borderRadius: 12, padding: '2px 10px', fontWeight: 500, fontSize: 12 }}>{t.assigned_to_nom}</span>
                      : <span style={{ color: 'var(--gray-400)', fontSize: 12 }}>—</span>}
                  </td>
                  <td>
                    {t.priorite === 'urgente' && <span className="badge badge-red">Urgente</span>}
                    {t.priorite === 'haute' && <span className="badge badge-orange">Haute</span>}
                    {t.priorite === 'normale' && <span className="badge badge-blue">Normale</span>}
                    {t.priorite === 'basse' && <span className="badge badge-gray">Basse</span>}
                  </td>
                  <td><span className={'badge ' + (TACHE_STATUT_BADGE[t.statut] || 'badge-gray')}>{TACHE_STATUT_LABELS[t.statut] || t.statut}</span></td>
                  <td style={{ fontSize: 13, color: 'var(--gray-500)' }}>
                    {t.date_echeance ? new Date(t.date_echeance).toLocaleDateString('fr-FR') : '—'}
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {t.statut === 'termine' && (
                      <button className="btn btn-green btn-sm" onClick={() => validerTache(t.id)}>Valider</button>
                    )}
                    {t.statut === 'termine' && (
                      <button className="btn btn-outline btn-sm" style={{ marginLeft: 4, color: '#c05621', borderColor: '#c05621' }} onClick={() => marquerReserve(t.id)}>⚠️ Reserve</button>
                    )}
                    {t.statut === 'reserve' && (
                      <span style={{ fontSize: 11, color: '#c05621', fontWeight: 600 }}>⚠️ A lever</span>
                    )}
                    <button className="btn btn-outline btn-sm" style={{ marginLeft: 4 }} onClick={() => setEditerTache(t)}>✏️</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {editerTache && (
        <ModalEditerTache
          tache={editerTache}
          chantiers={chantiers}
          onClose={() => setEditerTache(null)}
          onSaved={() => { onRefresh(); setEditerTache(null) }}
        />
      )}
    </div>
  )
}

// ─── Vue Documents ────────────────────────────────────────────────────────────

function VueDocuments({ chantiers }) {
  const [documents, setDocuments] = useState([])
  const [loading, setLoading] = useState(true)
  const [chantierId, setChantierId] = useState('')

  const loadDocs = useCallback(async () => {
    setLoading(true)
    let query = supabase.from('documents').select('*').order('created_at', { ascending: false })
    if (chantierId) query = query.eq('chantier_id', chantierId)
    const { data } = await query
    setDocuments(data || [])
    setLoading(false)
  }, [chantierId])

  useEffect(() => { loadDocs() }, [loadDocs])

  const handleUpload = async (e) => {
    const file = e.target.files[0]
    if (!file) return
    if (file.size > 50 * 1024 * 1024) {
      alert('Fichier trop volumineux. La taille maximale autorisée est 50 Mo.')
      e.target.value = ''
      return
    }
    const path = 'documents/' + Date.now() + '_' + file.name
    const { error: uploadError } = await supabase.storage.from('chantier-docs').upload(path, file)
    if (uploadError) { alert('Erreur upload: ' + uploadError.message); return }
    const { data: urlData } = supabase.storage.from('chantier-docs').getPublicUrl(path)
    const { error: insertError } = await supabase.from('documents').insert([{
      nom: file.name, url: urlData.publicUrl, type: file.type,
      taille: file.size, chantier_id: chantierId || null,
    }])
    if (insertError) { alert('Erreur sauvegarde doc : ' + insertError.message); return }
    loadDocs()
  }

  return (
    <div>
      <div className="section-header">
        <h2 className="section-title">Documents</h2>
        <label className="btn btn-green" style={{ cursor: 'pointer' }}>
          + Importer
          <input type="file" hidden onChange={handleUpload} />
        </label>
      </div>
      <div style={{ marginBottom: 16 }}>
        <select className="form-input" style={{ maxWidth: 280 }} value={chantierId}
          onChange={e => setChantierId(e.target.value)}>
          <option value="">Tous les chantiers</option>
          {chantiers.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
        </select>
      </div>
      {loading ? <div className="loading-state">Chargement...</div> : documents.length === 0 ? (
        <div className="empty-state"><div className="empty-state-icon">📄</div>
          <div className="empty-state-text">Aucun document importe.</div></div>
      ) : (
        <div className="card">
          <table className="data-table">
            <thead><tr><th>Nom</th><th>Type</th><th>Taille</th><th>Date</th><th>Action</th></tr></thead>
            <tbody>
              {documents.map(doc => (
                <tr key={doc.id}>
                  <td style={{ fontWeight: 500 }}>{doc.nom}</td>
                  <td><span className="badge badge-gray">{doc.type ? doc.type.split('/')[1].toUpperCase() : 'Fichier'}</span></td>
                  <td style={{ fontSize: 13, color: 'var(--gray-500)' }}>{formatSize(doc.taille)}</td>
                  <td style={{ fontSize: 13, color: 'var(--gray-500)' }}>{doc.created_at ? new Date(doc.created_at).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                  <td><a href={doc.url} target="_blank" rel="noreferrer" className="btn btn-outline btn-sm">Telecharger</a></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ─── Vue Photos ───────────────────────────────────────────────────────────────

function VuePhotos({ chantiers }) {
  const [photos, setPhotos] = useState([])
  const [loading, setLoading] = useState(true)
  const [chantierId, setChantierId] = useState('')
  const [preview, setPreview] = useState(null)

  const loadPhotos = useCallback(async () => {
    setLoading(true)
    let query = supabase.from('photos').select('*').order('created_at', { ascending: false })
    if (chantierId) query = query.eq('chantier_id', chantierId)
    const { data } = await query
    setPhotos(data || [])
    setLoading(false)
  }, [chantierId])

  useEffect(() => { loadPhotos() }, [loadPhotos])

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files)
    for (const file of files) {
      const path = 'photos/' + Date.now() + '_' + file.name.replace(/[^a-zA-Z0-9._-]/g, '_')
      const { error: uploadError } = await supabase.storage.from('chantier-photos').upload(path, file)
      if (uploadError) { alert('Erreur upload photo : ' + uploadError.message); continue }
      const { data: urlData } = supabase.storage.from('chantier-photos').getPublicUrl(path)
      const { error: insertError } = await supabase.from('photos').insert([{ url: urlData.publicUrl, chantier_id: chantierId || null, description: file.name }])
      if (insertError) alert('Erreur sauvegarde photo : ' + insertError.message)
    }
    loadPhotos()
  }

  return (
    <div>
      <div className="section-header">
        <h2 className="section-title">Photos de chantier</h2>
        <label className="btn btn-green" style={{ cursor: 'pointer' }}>
          + Ajouter des photos
          <input type="file" hidden multiple accept="image/*" onChange={handleUpload} />
        </label>
      </div>
      <div style={{ marginBottom: 16 }}>
        <select className="form-input" style={{ maxWidth: 280 }} value={chantierId}
          onChange={e => setChantierId(e.target.value)}>
          <option value="">Tous les chantiers</option>
          {chantiers.map(c => <option key={c.id} value={c.id}>{c.nom}</option>)}
        </select>
      </div>
      {loading ? <div className="loading-state">Chargement...</div> : photos.length === 0 ? (
        <div className="empty-state"><div className="empty-state-icon">📷</div>
          <div className="empty-state-text">Aucune photo pour ce chantier.</div></div>
      ) : (
        <div className="photo-grid">
          {photos.map(p => (
            <div key={p.id} className="photo-item" onClick={() => setPreview(p)}>
              <img src={p.url} alt={p.description || 'Photo'} loading="lazy" />
            </div>
          ))}
        </div>
      )}
      {preview && (
        <div className="modal-overlay" onClick={() => setPreview(null)}>
          <div style={{ maxWidth: 800, width: '100%', background: 'white', borderRadius: 14, overflow: 'hidden' }}>
            <img src={preview.url} alt={preview.description} style={{ width: '100%', display: 'block' }} />
            <div style={{ padding: '12px 16px', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-outline btn-sm" onClick={() => setPreview(null)}>Fermer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Vue Intervenants ─────────────────────────────────────────────────────────

function VueIntervenants() {
  const [profiles, setProfiles] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    supabase.from('profiles').select('*').neq('role', 'client').then(({ data }) => {
      setProfiles(data || [])
      setLoading(false)
    })
  }, [])

  if (loading) return <div className="loading-state">Chargement...</div>

  return (
    <div>
      <div className="section-header">
        <h2 className="section-title">Intervenants ({profiles.length})</h2>
      </div>
      {profiles.length === 0 ? (
        <div className="empty-state"><div className="empty-state-icon">👥</div>
          <div className="empty-state-text">Aucun intervenant enregistre.</div></div>
      ) : (
        <div className="card">
          <table className="data-table">
            <thead><tr><th>Nom</th><th>Email</th><th>Role</th></tr></thead>
            <tbody>
              {profiles.map(p => (
                <tr key={p.id}>
                  <td style={{ fontWeight: 500 }}>{p.prenom} {p.nom}</td>
                  <td style={{ color: 'var(--gray-500)', fontSize: 13 }}>{p.email}</td>
                  <td><span className={'badge ' + (p.role === 'architecte' ? 'badge-green' : p.role === 'entreprise' ? 'badge-blue' : 'badge-gray')}>{p.role}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ─── Dashboard Vue d'ensemble ─────────────────────────────────────────────────

function DashboardOverview({ chantiers, taches, profile, onNavigate, abonnementExpire, onDemandeRenouvellement }) {
  const enCours = chantiers.filter(c => c.statut === 'en_cours').length
  const tachesAValider = taches.filter(t => t.statut === 'termine').length
  const tachesEnReserve = taches.filter(t => t.statut === 'reserve').length // eslint-disable-line no-unused-vars

  // Info abonnement
  const dateExp = profile && profile.date_expiration ? new Date(profile.date_expiration) : null
  const joursRestants = dateExp ? Math.ceil((dateExp - new Date()) / (1000 * 60 * 60 * 24)) : null

  return (
    <div>
      {/* Bandeau abonnement */}
      {joursRestants !== null && (
        <div style={{
          padding: '14px 20px', borderRadius: 10, marginBottom: 20,
          background: abonnementExpire ? '#fff5f5' : joursRestants <= 7 ? '#fffbeb' : '#e8f7f2',
          border: '1px solid ' + (abonnementExpire ? '#fed7d7' : joursRestants <= 7 ? '#fef3c7' : '#b8e8d8'),
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
        }}>
          <div>
            <span style={{ fontWeight: 600, fontSize: 13, color: abonnementExpire ? '#c53030' : joursRestants <= 7 ? '#92400e' : '#1D9E75' }}>
              Abonnement {abonnementExpire ? 'expire' : 'actif'}
            </span>
            {dateExp && (
              <span style={{ fontSize: 12, color: '#718096', marginLeft: 8 }}>
                {abonnementExpire ? 'Expire le ' : 'Expire le '}
                {dateExp.toLocaleDateString('fr-FR')}
                {joursRestants > 0 && (' (' + joursRestants + ' j restants)')}
              </span>
            )}
          </div>
          <button
            onClick={onDemandeRenouvellement}
            style={{
              background: abonnementExpire ? '#e53e3e' : '#1D9E75', color: 'white',
              border: 'none', borderRadius: 6, padding: '5px 14px',
              fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}
          >
            {abonnementExpire ? 'Renouveler' : 'Renouveler - 20€/mois'}
          </button>
        </div>
      )}

      <div id="step-dashboard-stats" className="stats-grid">
        <div className="stat-card">
          <div className="stat-card-header">
            <div><div className="stat-value">{chantiers.length}</div><div className="stat-label">Chantiers total</div></div>
            <div className="stat-icon" style={{ background: 'var(--primary-light)', color: 'var(--primary)' }}>🏗️</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header">
            <div><div className="stat-value" style={{ color: 'var(--info)' }}>{enCours}</div><div className="stat-label">En cours</div></div>
            <div className="stat-icon" style={{ background: 'var(--info-bg)', color: 'var(--info)' }}>🔄</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header">
            <div><div className="stat-value" style={{ color: '#c05621' }}>{taches.filter(t => t.statut === 'en_attente').length}</div><div className="stat-label">Taches en attente</div></div>
            <div className="stat-icon" style={{ background: 'var(--warning-bg)', color: '#c05621' }}>⏳</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-header">
            <div><div className="stat-value" style={{ color: 'var(--danger)' }}>{tachesAValider}</div><div className="stat-label">A valider</div></div>
            <div className="stat-icon" style={{ background: '#fff5f5', color: 'var(--danger)' }}>✅</div>
          </div>
        </div>
      </div>

      <div className="grid-2" style={{ gap: 20 }}>
        <div className="card">
          <div className="card-header">
            <span className="card-title">Chantiers en cours</span>
            <button className="btn btn-outline btn-sm" onClick={() => onNavigate('chantiers')}>Voir tout</button>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {chantiers.filter(c => c.statut === 'en_cours').length === 0 ? (
              <div className="empty-state" style={{ padding: '24px' }}>
                <div className="empty-state-text">Aucun chantier en cours</div>
              </div>
            ) : (
              chantiers.filter(c => c.statut === 'en_cours').slice(0, 5).map(c => (
                <div key={c.id} style={{ padding: '14px 20px', borderBottom: '1px solid var(--gray-100)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                    <span style={{ fontWeight: 500, fontSize: 14 }}>{c.nom}</span>
                    <span style={{ fontSize: 13, color: 'var(--primary)', fontWeight: 600 }}>{c.progression || 0}%</span>
                  </div>
                  <div className="progress-bar-wrap">
                    <div className="progress-bar-fill" style={{ width: (c.progression || 0) + '%' }} />
                  </div>
                  {c.code && (
                    <div style={{ fontSize: 11, color: '#a0aec0', marginTop: 6, fontFamily: 'monospace' }}>
                      Code : <strong style={{ color: '#1D9E75', letterSpacing: 2 }}>{c.code}</strong>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <span className="card-title">Taches a valider</span>
            <button className="btn btn-outline btn-sm" onClick={() => onNavigate('taches')}>Voir tout</button>
          </div>
          <div className="card-body" style={{ padding: 0 }}>
            {tachesAValider === 0 ? (
              <div className="empty-state" style={{ padding: '24px' }}>
                <div className="empty-state-text">Aucune tache a valider</div>
              </div>
            ) : (
              taches.filter(t => t.statut === 'termine').slice(0, 5).map(t => (
                <div key={t.id} style={{ padding: '12px 20px', borderBottom: '1px solid var(--gray-100)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 500, fontSize: 14 }}>{t.titre}</div>
                    {t.date_echeance && <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>{new Date(t.date_echeance).toLocaleDateString('fr-FR')}</div>}
                  </div>
                  <span className="badge badge-orange">A valider</span>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Vue Messages ────────────────────────────────────────────────────────────

function VueMessages({ chantiers, user, profile }) {
  const [selectedId, setSelectedId] = React.useState(null)
  const [canal, setCanal] = React.useState('interne')

  const nomArchi = profile
    ? ((profile.prenom || '') + ' ' + (profile.nom || '')).trim() || (user && user.email) || 'Architecte'
    : (user && user.email) || 'Architecte'

  React.useEffect(() => {
    if (chantiers.length > 0 && !selectedId) setSelectedId(chantiers[0].id)
  }, [chantiers, selectedId])

  const chantier = chantiers.find(c => c.id === selectedId)

  return (
    <div style={{ display: 'flex', gap: 20, height: 'calc(100vh - 160px)', minHeight: 500 }}>
      {/* Panneau liste chantiers */}
      <div style={{ width: 250, background: 'white', borderRadius: 12, overflow: 'hidden', boxShadow: '0 1px 4px rgba(0,0,0,0.07)', flexShrink: 0, display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '16px 18px', borderBottom: '1px solid #e2e8f0', fontWeight: 700, fontSize: 14, color: '#2d3748' }}>Chantiers</div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {chantiers.length === 0
            ? <div style={{ padding: 20, color: '#a0aec0', fontSize: 13 }}>Aucun chantier</div>
            : chantiers.map(c => (
              <button key={c.id} onClick={() => { setSelectedId(c.id); setCanal('interne') }} style={{
                display: 'block', width: '100%', padding: '12px 18px', border: 'none', cursor: 'pointer',
                textAlign: 'left', background: selectedId === c.id ? '#e8f7f2' : 'white',
                borderLeft: selectedId === c.id ? '3px solid #1D9E75' : '3px solid transparent',
                borderBottom: '1px solid #f5f6fa', transition: 'all 0.1s',
              }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: selectedId === c.id ? '#1D9E75' : '#2d3748' }}>{c.nom}</div>
                <div style={{ fontSize: 11, color: '#a0aec0', marginTop: 2 }}>
                  {c.statut === 'en_cours' ? 'En cours' : c.statut === 'planifie' ? 'Planifie' : c.statut}
                </div>
              </button>
            ))
          }
        </div>
      </div>

      {/* Zone chat */}
      {chantier ? (
        <div style={{ flex: 1, background: 'white', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Header + tabs canaux */}
          <div style={{ padding: '14px 20px', borderBottom: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <div style={{ fontWeight: 600, fontSize: 15, color: '#2d3748' }}>{chantier.nom}</div>
            <div style={{ display: 'flex', gap: 4, background: '#f5f6fa', borderRadius: 8, padding: 3 }}>
              {[
                { key: 'interne', label: 'Discussion interne', icon: '🔒' },
                { key: 'client', label: 'Message client', icon: '👤' },
              ].map(tab => (
                <button key={tab.key} onClick={() => setCanal(tab.key)} style={{
                  padding: '6px 12px', border: 'none', borderRadius: 6, cursor: 'pointer',
                  fontSize: 12, fontWeight: canal === tab.key ? 600 : 400,
                  background: canal === tab.key ? 'white' : 'transparent',
                  color: canal === tab.key ? '#1D9E75' : '#718096',
                  boxShadow: canal === tab.key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 5,
                }}>
                  {tab.icon} {tab.label}
                </button>
              ))}
            </div>
          </div>
          {/* Info bande canal */}
          <div style={{
            fontSize: 11, color: canal === 'interne' ? '#92400e' : '#276749',
            padding: '5px 20px',
            background: canal === 'interne' ? '#fffbeb' : '#f0fff4',
            borderBottom: '1px solid ' + (canal === 'interne' ? '#fde68a' : '#c6f6d5'),
            flexShrink: 0,
          }}>
            {canal === 'interne'
              ? 'Visible uniquement par l\'architecte et les entreprises membres du chantier'
              : 'Visible par l\'architecte et le client du chantier'}
          </div>
          <ChantierChat
            chantierId={chantier.id}
            canal={canal}
            currentUserId={user && user.id}
            currentUserNom={nomArchi}
          />
        </div>
      ) : (
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white', borderRadius: 12 }}>
          <div style={{ textAlign: 'center', color: '#a0aec0' }}>
            <div style={{ fontSize: 40, marginBottom: 8 }}>💬</div>
            <div style={{ fontSize: 14 }}>Selectionnez un chantier pour voir les messages</div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Vue Notifications ────────────────────────────────────────────────────────

function VueNotifications({ userId, onResetCount }) {
  const [notifications, setNotifications] = React.useState([])
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    if (!userId) return
    supabase.from('notifications').select('*').eq('user_id', userId)
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        setNotifications(data || [])
        setLoading(false)
        // Marquer toutes comme lues
        supabase.from('notifications').update({ lu: true }).eq('user_id', userId).eq('lu', false)
          .then(() => { if (onResetCount) onResetCount() })
      })
  }, [userId, onResetCount])

  if (loading) return <div className="loading-state">Chargement...</div>

  return (
    <div>
      <div className="section-header">
        <h2 className="section-title">Notifications ({notifications.length})</h2>
      </div>
      {notifications.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">🔔</div>
          <div className="empty-state-text">Aucune notification pour l'instant</div>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {notifications.map(n => (
            <div key={n.id} style={{
              background: n.lu ? 'white' : '#f0fff4',
              border: '1.5px solid ' + (n.lu ? '#e2e8f0' : '#c6f6d5'),
              borderRadius: 10, padding: '14px 18px',
              display: 'flex', gap: 14, alignItems: 'flex-start',
            }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: n.lu ? '#e2e8f0' : '#1D9E75', marginTop: 6, flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: '#2d3748', marginBottom: 3 }}>{n.titre}</div>
                <div style={{ fontSize: 13, color: '#718096', lineHeight: 1.55 }}>{n.contenu}</div>
                <div style={{ fontSize: 11, color: '#a0aec0', marginTop: 6 }}>
                  {new Date(n.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })}
                  {' a '}
                  {new Date(n.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ─── Composant Principal ──────────────────────────────────────────────────────

export default function ArchitecteDashboard({ activePage, onNavigate, user, profile, abonnementExpire, onDemandeRenouvellement, isDemoMode = false, demoData = null, onRelancerTutoriel }) {
  const [chantiers, setChantiers] = useState([])
  const [taches, setTaches] = useState([])
  const [loading, setLoading] = useState(true)
  const [showNewChantier, setShowNewChantier] = useState(false)
  const [showNewTache, setShowNewTache] = useState(false)
  const [notifCount, setNotifCount] = useState(0) // eslint-disable-line no-unused-vars
  const [showOnboarding, setShowOnboarding] = useState(() => !isDemoMode && !isOnboardingDone('architecte'))

  const loadData = useCallback(async () => {
    if (isDemoMode && demoData) {
      setChantiers(demoData.chantiers || [])
      setTaches(demoData.taches || [])
      setLoading(false)
      return
    }
    setLoading(true)
    const [chantiersRes, tachesRes] = await Promise.all([
      supabase.from('chantiers').select('*').eq('architecte_id', user && user.id).order('created_at', { ascending: false }),
      supabase.from('taches').select('*').order('created_at', { ascending: false }),
    ])
    setChantiers(chantiersRes.data || [])
    setTaches(tachesRes.data || [])
    setLoading(false)
  }, [isDemoMode, demoData, user])

  const loadNotifCount = useCallback(async () => {
    if (!user || !user.id) return
    const { count } = await supabase.from('notifications')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', user.id).eq('lu', false)
    setNotifCount(count || 0)
  }, [user])

  useEffect(() => { loadData() }, [loadData])
  useEffect(() => { loadNotifCount() }, [loadNotifCount])

  const handleNouveauChantier = () => {
    if (isDemoMode) return
    if (abonnementExpire) { onDemandeRenouvellement(); return }
    setShowNewChantier(true)
  }

  const renderPage = () => {
    switch (activePage) {
      case 'dashboard': return <DashboardOverview chantiers={chantiers} taches={taches} profile={profile} onNavigate={onNavigate} abonnementExpire={abonnementExpire} onDemandeRenouvellement={onDemandeRenouvellement} />
      case 'chantiers': return <VueChantiers chantiers={chantiers} taches={taches} loading={loading} onNew={handleNouveauChantier} onRefresh={loadData} abonnementExpire={abonnementExpire} onDemandeRenouvellement={onDemandeRenouvellement} />
      case 'taches': return <VueTaches taches={taches} chantiers={chantiers} loading={loading} onNew={() => setShowNewTache(true)} onRefresh={loadData} />
      case 'documents': return <VueDocuments chantiers={chantiers} />
      case 'photos': return <VuePhotos chantiers={chantiers} />
      case 'intervenants': return <VueIntervenants />
      case 'messages': return <VueMessages chantiers={chantiers} user={user} profile={profile} />
      case 'notifications': return <VueNotifications userId={user && user.id} onResetCount={() => setNotifCount(0)} />
      default: return <DashboardOverview chantiers={chantiers} taches={taches} profile={profile} onNavigate={onNavigate} abonnementExpire={abonnementExpire} onDemandeRenouvellement={onDemandeRenouvellement} />
    }
  }

  const handleRelancerTutoriel = () => {
    resetOnboarding('architecte')
    setShowOnboarding(true)
  }

  // Exposer la fonction au parent (via onRelancerTutoriel)
  useEffect(() => {
    if (onRelancerTutoriel) onRelancerTutoriel(handleRelancerTutoriel)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      {renderPage()}
      {showNewChantier && (
        <ModalNouveauChantier onClose={() => setShowNewChantier(false)} onCreated={loadData} userId={user && user.id} profile={profile} />
      )}
      {showNewTache && (
        <ModalNouvelleTache onClose={() => setShowNewTache(false)} onCreated={loadData} chantiers={chantiers} />
      )}
      {showOnboarding && !isDemoMode && (
        <OnboardingTour role="architecte" onTermine={() => setShowOnboarding(false)} />
      )}
    </div>
  )
}

function formatSize(bytes) {
  if (!bytes) return '—'
  if (bytes < 1024) return bytes + ' o'
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' Ko'
  return (bytes / (1024 * 1024)).toFixed(1) + ' Mo'
}